"use client";

import React from "react";

import { Contact14Block as Contact14BlockType,  Media as MediaProps } from '@/payload-types';
import { Media } from "@/components/Media";
import { CMSLink } from "@/components/Link";
import RevealHeading from "@/components/RevealHeading";
import FramerReveal from "@/components/framer-reveal-component";
import { TypedLocale } from "payload";

export const Contact14Block: React.FC<Contact14BlockType
  & {
    id?: string,
    locale: TypedLocale
  }
> = ({
  theme, contacts, description, heading, map, tagline
}) => {
    // Function to extract src from iframe
    const extractSRC = (mapHTML: string) => {
      // Use a simple regex to extract the src attribute from the iframe
      const srcMatch = mapHTML.match(/src="([^"]+)"/);
      return srcMatch ? srcMatch[1] : '';
    };
    // Extract the src from the map prop
    const extractedSRC = map ? extractSRC(map) : '';

    return (
      <section data-theme={theme} className="bg-background py-16 md:py-24 lg:py-28">
        <div className="container">
          <div className="rb-12 mb-12 max-w-lg md:mb-18 lg:mb-20">
            <RevealHeading>
              <p className="mb-3 font-semibold md:mb-4 tagline">{tagline}</p>
            </RevealHeading>
            <RevealHeading>
              <h2 data-animate="animate-from:opacity-0" className="rb-5 mb-5 text-5xl font-bold md:mb-6 md:text-7xl lg:text-8xl">
                {heading}
              </h2>
            </RevealHeading>
            <RevealHeading>
              <p className="md:text-md">{description}</p>
            </RevealHeading>
          </div>
          <div className="grid auto-cols-fr grid-cols-1 gap-x-12 gap-y-12 md:grid-cols-[0.5fr_1fr] md:gap-x-20 md:gap-y-16">

            <div className="contacts space-y-8 md:space-y-12">
              {contacts?.map((contact, index) => (
                <div key={index} className="contact">
                  {contact.icon && (
                    <FramerReveal>
                      <div className="mb-4">
                        {(contact.icon as MediaProps).url?.endsWith('.svg') ?  // For SVG files
                          <div
                            className="svgIcon svgIcon-medium" // Add size variant class
                            style={{
                              width: '3rem',
                              height: '3rem',
                              maskImage: `url("${encodeURI((contact.icon as MediaProps)?.url as string)}")`,
                              WebkitMaskImage: `url("${encodeURI((contact.icon as MediaProps)?.url as string)}")`,
                              maskRepeat: 'no-repeat',
                              maskSize: 'contain',
                              backgroundColor: 'var(--accent)',
                            }}
                          />
                          : <Media resource={contact.icon!} imgClassName="w-12 h-12 mb-4" />
                        }
                      </div>
                    </FramerReveal>
                  )}

                  <RevealHeading>
                    <h3 className="mb-2 text-md font-bold leading-[1.4] md:text-xl">{contact.title}</h3>
                  </RevealHeading>

                  <RevealHeading>
                    <p className="mb-2">{contact.description}</p>
                  </RevealHeading>
                  
                  <div className="flex gap-2 flex-col">
                    {
                      contact.enableLink && contact.links?.map((link, linkIndex) => (
                        <FramerReveal key={linkIndex}>
                          <CMSLink {...link.link} className="inline-flex items-center gap-2">
                            {
                              link.link.fiIcon && (
                                <i className={`mt-2 fi ${link.link.fiIcon}`} />
                              )
                            }
                          </CMSLink>
                        </FramerReveal>
                      ))
                    }
                  </div>
                </div>
              ))}
            </div>

            <div className="justify-self-end w-full min-h-[300px] md:min-h-[400px]">
              {extractedSRC && (
                <iframe
                  src={extractedSRC}
                  loading="lazy"
                  width={'100%'}
                  height={'100%'}
                  referrerPolicy="no-referrer-when-downgrade"
                ></iframe>
              )}
            </div>
          </div>
        </div>

      </section>
    );
  };

export default Contact14Block;